import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useMemo } from "react"
import { useForm } from "react-hook-form"

import { DESTINATION_DEFAULT_FORM_VALUES } from "../../../constants"
import { useInventoryDestinationFields } from "../../../hooks"
import { type InventoryDestinationFormValues, inventoryDestinationFormSchema } from "../../../schemas"
import type { TransferFormProductInventory } from "../types"
import { getSelectedShelvingIds } from "../utils/transfer"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { AerosTextField } from "@/components/UiKit"
import { FormAutocomplete } from "@/components/UiKit/FormControls/FormAutocomplete"
import { FormSelect } from "@/components/UiKit/FormControls/FormSelect"
import { BIN_SIZE_OPTIONS } from "@/constants/warehouse"
import type { WarehouseArea } from "@/graphql/codegen/graphql"
import { formatWarehouseLabel } from "@/utils/warehouse"

interface TransferPalletDestinationOverrideDialogProps {
  inventories: TransferFormProductInventory[]
  productId: number
  palletReferenceNumber: number
  warehouseArea: WarehouseArea
  onClose: () => void
  onConfirm: (destination: InventoryDestinationFormValues) => void
}

export const TransferPalletDestinationOverrideDialog: FC<TransferPalletDestinationOverrideDialogProps> = ({
  inventories,
  productId,
  palletReferenceNumber,
  warehouseArea,
  onClose,
  onConfirm,
}) => {
  const pallet = inventories
    .find((inventory) => inventory.product.productId === productId)!
    .pallets.find((pallet) => pallet.referenceNbr === palletReferenceNumber)!

  const selectedShelvingIds = useMemo(
    () => getSelectedShelvingIds({ inventories, palletReferenceNumber }),
    [inventories, palletReferenceNumber]
  )

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isValid },
  } = useForm<InventoryDestinationFormValues>({
    resolver: zodResolver(inventoryDestinationFormSchema),
    defaultValues: pallet.destination ?? DESTINATION_DEFAULT_FORM_VALUES,
    mode: "onChange",
  })

  const {
    fieldPaths,
    aisleCode,
    sectionCode,
    shouldShowShelvingFields,
    holdAreaOptions,
    aisleOptions,
    shelfOptions,
    binOptions,
    isFetching,
    isAisleFull,
    isSectionFull,
    handleHoldAreaChange,
    handleBinSizeChange,
    handleAisleCodeChange,
    handleSectionCodeChange,
    handleBinNumberChange,
  } = useInventoryDestinationFields<InventoryDestinationFormValues>({
    watch,
    setValue,
    warehouseArea,
    selectedShelvingIds,
    autoSelectFirstAvailable: false,
  })

  const handleConfirm = handleSubmit((formValues) => {
    onConfirm(formValues)
  })

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth data-testid='transfer.pallet-destination-override-dialog'>
      <DialogHeader title='Override Location' onClose={onClose} />
      <DialogContent className='flex flex-col gap-4'>
        <AerosTextField
          label='Warehouse'
          value={formatWarehouseLabel(warehouseArea)}
          disabled
          size='small'
          data-testid='transfer.pallet-destination-override-dialog.warehouse-field'
        />
        <FormSelect
          control={control}
          name={fieldPaths.holdArea}
          label='Area'
          required
          placeholder='Select one'
          options={holdAreaOptions}
          onChange={handleHoldAreaChange}
          data-testid='transfer.pallet-destination-override-dialog.area-field'
        />
        {shouldShowShelvingFields && (
          <>
            <FormAutocomplete
              control={control}
              name={fieldPaths.binSize}
              label='Bin Size'
              placeholder='Select one'
              options={BIN_SIZE_OPTIONS}
              onChange={handleBinSizeChange}
              data-testid='transfer.pallet-destination-override-dialog.bin-size-field'
            />
            <FormAutocomplete
              control={control}
              name={fieldPaths.aisleCode}
              label='Aisle'
              required
              placeholder='Select one'
              options={aisleOptions}
              disabled={isFetching}
              noOptionsText='No aisles'
              error={isAisleFull}
              helperText={isAisleFull ? "No available bins in this aisle" : undefined}
              onChange={handleAisleCodeChange}
              data-testid='transfer.pallet-destination-override-dialog.aisle-field'
            />
            <FormAutocomplete
              control={control}
              name={fieldPaths.sectionCode}
              label='Shelf'
              required
              placeholder='Select one'
              options={shelfOptions}
              disabled={!aisleCode || isFetching}
              noOptionsText='No shelves'
              error={isSectionFull}
              helperText={isSectionFull ? "No available bins on this shelf" : undefined}
              onChange={handleSectionCodeChange}
              data-testid='transfer.pallet-destination-override-dialog.shelf-field'
            />
            <FormAutocomplete
              control={control}
              name={fieldPaths.binNumber}
              label='Bin'
              required
              placeholder='Select one'
              options={binOptions}
              disabled={!sectionCode || isFetching}
              noOptionsText='No bins'
              onChange={handleBinNumberChange}
              data-testid='transfer.pallet-destination-override-dialog.bin-field'
            />
          </>
        )}
      </DialogContent>
      <DialogActions className='justify-between px-6 pb-6'>
        <Button
          appearance='outlined'
          onClick={onClose}
          data-testid='transfer.pallet-destination-override-dialog.cancel-button'
        >
          Cancel
        </Button>
        <Button
          appearance='contained'
          disabled={!isValid}
          onClick={handleConfirm}
          data-testid='transfer.pallet-destination-override-dialog.confirm-button'
        >
          Confirm Override
        </Button>
      </DialogActions>
    </Dialog>
  )
}
